import queryString from 'query-string'
import { LoaderBackdrop } from 'asterX-UI/Loaders'
import { jwtDecode } from 'jwt-decode'
import { AuthService } from 'lib/api'
import { PROJECT_CODE } from 'lib/constants/roles'
import { useResErrors, useRoutes } from 'lib/hooks'
import { useModalbar } from 'lib/modules/Modalbar'
import { deepCopyObject, getUserRoleByToken, LocalStorageService } from 'lib/utils'
import { createContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { HOME_PATH, SIGN_IN_PATH } from 'routes/constants.routes'
import { useMediaQuery, useTheme } from '@mui/material'

const initialState = {
  token: '',
  role: null,
  data: {}
}

export const AuthStoreContext = createContext({
  authStore: {},
  authStoreFunc: {}
})

const AuthStoreProvider = ({ children }) => {
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const navigate = useNavigate()
  const routes = useRoutes()

  const { t } = useTranslation()
  const { standardCheck } = useResErrors()
  const { callModalbar } = useModalbar()

  const _isMounted = useRef(true)

  const [appBar, setAppBar] = useState(true)
  const [user, setUser] = useState(initialState)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [loading, setLoading] = useState(false)
  const [initLoading, setInitLoading] = useState(false)

  useEffect(() => {
    initEffect()

    return () => {
      _isMounted.current = false
    }
  }, [])

  const initEffect = async () => {
    const values = queryString.parse(location.search)
    if (values && values.token) {
      await setTokenFromQuery(values.token)
      setAppBar(false)
    }

    if (user?.token) {
      await setTokenFromQuery(user.token)
    } else {
      await setTokenFromLS()
    }
  }

  const logout = () => {
    AuthService.setToken(null)
    setUser(deepCopyObject(initialState))
    setIsLoggedIn(false)
    navigate(SIGN_IN_PATH)
  }

  const getUserRole = roles => (roles ? roles?.find(el => el.project.code.includes(PROJECT_CODE)) : null)
  const isThisProject = user => user?.roleList?.some(el => el.project.code.includes(PROJECT_CODE))

  const setDataToStorage = async (token = '', isReset = false) => {
    AuthService.setToken(token)
    let role = null
    if (isReset) {
      setUser(deepCopyObject(initialState))
      setIsLoggedIn(false)
      return
    }
    setInitLoading(true)

    try {
      await AuthService.getUserByToken().then(res =>
        standardCheck(
          res,
          async () => {
            setUser(prevState => ({
              ...prevState,
              token,
              data: res.data,
              role: getUserRole(res.data?.roles)
            }))

            setIsLoggedIn(!!token && !!res?.data?.id)
          },
          {
            onFail: () => {
              role = null
              setIsLoggedIn(false)
              AuthService.setToken()
              navigate(SIGN_IN_PATH)
            }
          }
        )
      )
    } catch (error) {
      console.error(error)
    } finally {
      setInitLoading(false)
    }

    return role
  }

  const setTokenFromQuery = async token => {
    if (!token) {
      setInitLoading(false)
      return false
    }
    try {
      const decoded = jwtDecode(token)
      if (decoded && isThisProject(decoded)) {
        setIsLoggedIn(true)

        routes.goTo(routes.lastVisit ? (routes.lastVisit !== SIGN_IN_PATH ? routes.lastVisit : HOME_PATH) : HOME_PATH)
        await setDataToStorage(token, false)

        return true
      } else {
        await setDataToStorage(null, true)
        return false
      }
    } catch (e) {
      return false
    } finally {
      setInitLoading(false)
    }
  }

  const setTokenFromLS = () => setTokenFromQuery(LocalStorageService.getAccessToken())

  const login = userData => {
    if (!userData?.username?.length || !userData?.password?.length) {
      return
    }

    AuthService.login(userData).then(res =>
      standardCheck(
        res,
        async () => {
          const { token } = res.data
          const decoded = jwtDecode(token)

          if (!!token && !!decoded && isThisProject(decoded)) {
            setIsLoggedIn(true)
            LocalStorageService.setAccessToken(token)
            setDataToStorage(token)
            routes.goTo(HOME_PATH)
          } else {
            callModalbar(t('errors.NotAccess_SignIn.title'), t('errors.NotAccess_SignIn.body'), { btnText: 'OK' })
          }
        },
        {
          onErrCustom: () => {
            callModalbar(t('errors.Unauthorized_SignIn.title'), t('errors.Unauthorized_SignIn.body'), { btnText: 'OK' })
          }
        }
      )
    )
  }

  const isUserRole = getUserRoleByToken(user.data)

  useEffect(() => {
    routes.setLastVisit(location.pathname)
  }, [location.pathname, routes])

  const authStore = {
    appBar,
    user,
    loading,
    isLoggedIn,
    isMobile,
    isUserRole
  }

  const authStoreFunc = {
    login,
    setLoading,
    logout,
    setTokenFromQuery,
    setTokenFromLS
  }

  return (
    <AuthStoreContext.Provider value={{ authStore, authStoreFunc }}>
      {initLoading ? <LoaderBackdrop loading={initLoading} /> : <>{children}</>}
      <LoaderBackdrop loading={loading} />
    </AuthStoreContext.Provider>
  )
}

export default AuthStoreProvider
